/**
 * Firebase Synchronization Service — Americano mode
 * Handles tournament data persistence with Firestore (collection: 'americano')
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db, COLLECTIONS } from '../firebase';

class FirebaseSyncService {
  constructor() {
    this.currentTournamentId = null;
    this.currentTournamentName = null;
    this.isCreatingTournament = false;
  }

  /**
   * Save the current tournament to Firestore
   */
  async saveTournament(tournamentState, userId, screenName = null) {
    if (!userId || !this.currentTournamentId) return false;

    try {
      const cleanedState = this.cleanStateForFirestore(tournamentState);
      const tournamentName = this.generateTournamentName();

      const tournamentData = {
        userId: userId,
        mode: 'americano',
        name: this.currentTournamentName || tournamentState.config?.tournamentName || tournamentName,
        updatedAt: serverTimestamp(),
        currentScreen: screenName || 'config',
        state: cleanedState
      };

      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, this.currentTournamentId);
      await setDoc(docRef, tournamentData, { merge: true });

      console.log(`✅ Americano tournament saved (screen: ${screenName || 'unknown'})`);
      return true;

    } catch (error) {
      console.error('❌ Error saving tournament:', error);
      throw error;
    }
  }

  /**
   * Load a tournament from Firestore
   */
  async loadTournament(tournamentId, userId) {
    if (!userId) return null;

    try {
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDocFromServer(docRef);

      if (!docSnap.exists()) throw new Error('Tournament not found');

      const data = docSnap.data();
      if (data.userId !== userId) throw new Error('Unauthorized access to tournament');

      this.currentTournamentId = tournamentId;
      this.currentTournamentName = data.name || null;
      localStorage.setItem('currentTournamentId', tournamentId);

      console.log(`✅ [Tournament] ID: ${tournamentId} | Nom: ${data.name || 'Sans nom'}`);
      return data;

    } catch (error) {
      console.error('❌ Error loading tournament:', error);
      throw error;
    }
  }

  /**
   * Create a new tournament in Firestore
   */
  async createNewTournament(tournamentState, userId, name = null) {
    if (!userId) return null;
    if (this.isCreatingTournament) return null;

    this.isCreatingTournament = true;

    try {
      const tournamentName = name || tournamentState?.config?.tournamentName || this.generateTournamentName();
      const cleanedState = this.cleanStateForFirestore(tournamentState);

      const tournamentData = {
        userId: userId,
        mode: 'americano',
        name: tournamentName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentScreen: 'config',
        state: cleanedState
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.TOURNAMENTS), tournamentData);

      this.currentTournamentId = docRef.id;
      this.currentTournamentName = tournamentName;
      localStorage.setItem('currentTournamentId', docRef.id);

      console.log(`✅ [Tournament] ID: ${docRef.id} | Nom: ${tournamentName}`);
      return docRef.id;

    } catch (error) {
      console.error('❌ Error creating tournament:', error);
      throw error;
    } finally {
      this.isCreatingTournament = false;
    }
  }

  /**
   * List all tournaments for a user
   */
  async listUserTournaments(userId) {
    if (!userId) return [];

    try {
      const q = query(
        collection(db, COLLECTIONS.TOURNAMENTS),
        where('userId', '==', userId),
        limit(50)
      );

      const querySnapshot = await getDocs(q);
      const tournaments = [];

      querySnapshot.forEach((doc) => {
        tournaments.push({ id: doc.id, ...doc.data() });
      });

      tournaments.sort((a, b) => {
        const timeA = a.updatedAt?.toDate?.()?.getTime() || 0;
        const timeB = b.updatedAt?.toDate?.()?.getTime() || 0;
        return timeB - timeA;
      });

      console.log(`✅ Found ${tournaments.length} Americano tournaments`);
      return tournaments;

    } catch (error) {
      console.error('❌ Error listing tournaments:', error);
      throw error;
    }
  }

  /**
   * Delete a tournament from Firestore
   */
  async deleteTournament(tournamentId, userId) {
    if (!userId) return false;

    try {
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) throw new Error('Tournament not found');
      if (docSnap.data().userId !== userId) throw new Error('Unauthorized');

      await deleteDoc(docRef);

      if (this.currentTournamentId === tournamentId) {
        this.currentTournamentId = null;
        localStorage.removeItem('currentTournamentId');
      }

      console.log('✅ Tournament deleted:', tournamentId);
      return true;

    } catch (error) {
      console.error('❌ Error deleting tournament:', error);
      throw error;
    }
  }

  /**
   * Rename a tournament
   */
  async renameTournament(tournamentId, newName, userId) {
    if (!userId || !newName?.trim()) throw new Error('Paramètres invalides');

    try {
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) throw new Error('Tournament not found');
      if (docSnap.data().userId !== userId) throw new Error('Unauthorized');

      await updateDoc(docRef, {
        name: newName.trim(),
        updatedAt: serverTimestamp()
      });

      this.currentTournamentName = newName.trim();
      console.log('✅ Tournament renamed:', newName);
      return true;

    } catch (error) {
      console.error('❌ Error renaming tournament:', error);
      throw error;
    }
  }

  /**
   * Duplicate a tournament (copy config and players only)
   */
  async duplicateTournament(tournamentId, userId) {
    if (!userId) return null;

    try {
      const sourceDocRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const sourceDoc = await getDoc(sourceDocRef);

      if (!sourceDoc.exists()) throw new Error('Tournament not found');
      const sourceData = sourceDoc.data();
      if (sourceData.userId !== userId) throw new Error('Unauthorized');

      const newName = `${sourceData.name || 'Tournoi'} (Copie)`;

      const newState = {
        mode: 'americano',
        config: { ...sourceData.state.config },
        players: sourceData.state.players || [],
        ranking: [],
        schedule: { rounds: [] },
        currentRound: 0,
        scheduleGenerated: false,
        tournamentStarted: false,
        tournamentFinished: false,
        startTime: null
      };

      const newTournamentData = {
        userId: userId,
        mode: 'americano',
        name: newName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentScreen: 'config',
        state: newState
      };

      const newDocRef = await addDoc(collection(db, COLLECTIONS.TOURNAMENTS), newTournamentData);
      console.log('✅ Tournament duplicated:', newDocRef.id);
      return newDocRef.id;

    } catch (error) {
      console.error('❌ Error duplicating tournament:', error);
      throw error;
    }
  }

  /**
   * Load the most recent tournament
   */
  async loadLastTournament(userId) {
    if (!userId) return null;

    try {
      const tournaments = await this.listUserTournaments(userId);
      if (tournaments.length === 0) return null;
      return await this.loadTournament(tournaments[0].id, userId);
    } catch (error) {
      console.error('❌ Error loading last tournament:', error);
      return null;
    }
  }

  /**
   * Clean state object for Firestore
   * Note: detailedScore IS persisted in Americano (unlike Montante)
   */
  cleanStateForFirestore(state) {
    return JSON.parse(JSON.stringify(state, (key, value) => {
      if (typeof value === 'function') return undefined;
      if (value instanceof Date) return value.toISOString();
      return value;
    }));
  }

  /**
   * Generate a tournament name based on current date
   */
  generateTournamentName() {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `Tournoi ${day}/${month}/${year} ${hours}:${minutes}`;
  }

  /**
   * Fetch only the name of a tournament (lightweight, no state import)
   */
  async getTournamentName(tournamentId, userId) {
    if (!tournamentId || !userId) return null;
    try {
      const docRef = doc(db, COLLECTIONS.TOURNAMENTS, tournamentId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return null;
      const data = docSnap.data();
      if (data.userId !== userId) return null;
      return data.name || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Clear current tournament reference
   */
  clearCurrentTournament() {
    this.currentTournamentId = null;
    this.currentTournamentName = null;
    localStorage.removeItem('currentTournamentId');
  }
}

const firebaseSyncService = new FirebaseSyncService();
export default firebaseSyncService;
